import {
    AutocompleteOptionsWithMetadata,
    AutocompleteSource,
    AutocompleteState,
    BaseItem,
    StateUpdater,
    createAutocomplete,
} from "@algolia/autocomplete-core";
import { createRedirectUrlPlugin } from "@algolia/autocomplete-plugin-redirect-url";
import {
    BaseSyntheticEvent,
    KeyboardEvent,
    MouseEvent,
    MutableRefObject,
    useCallback,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    PageTemplates,
    isPageTemplate,
} from "@utils/configuration/pageTemplate.helpers";
import { debouncePromise } from "@utils/general";
import { isDefined } from "@utils/helpers";
import { useSearchClient } from "@utils/hooks/algolia/useSearchClient.hook";
import { useSubmitAutocompleteSearchBox } from "@utils/hooks/useSubmitAutocompleteSearchBox";
import { parseUrl } from "@utils/search/routing/configurationFromUrl";

import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { AutocompleteRef } from "@components/Search/SearchAutocomplete/AutocompleteSearchBox/autocompleteSearchBox.container";
import { contentPagesSource } from "@components/Search/SearchAutocomplete/ContentPages/contentPages.source";
import { productsSource } from "@components/Search/SearchAutocomplete/Products/products.source";
import { querySuggestionsSource } from "@components/Search/SearchAutocomplete/QuerySuggestions/querySuggestions.source";
import {
    hasRecentSearches,
    recentSearchesSource,
} from "@components/Search/SearchAutocomplete/RecentSearches/recentSearches.source";

import { ProductAlgoliaModel } from "lib/@types/service/product/algolia/product.algolia.model";

import { getRedirectUrlFromState } from "../helpers/redirect.helpers";

export type AutocompleteActions = {
    refresh: () => Promise<void>;
    setActiveItemId: StateUpdater<number | null>;
    setQuery: StateUpdater<string>;
    submit: (query: string) => void;
};

const debouncedPromise = debouncePromise(
    (items: Array<AutocompleteSource<any> | boolean | undefined>) =>
        Promise.resolve(items),
);

const createSetAutocompleteOpen =
    (
        setIsOpenStateUpdaters: Array<(value: boolean) => void>,
        isOpen: boolean,
        afterClose?: () => void,
    ) =>
    (shouldOpen: boolean, force?: boolean) =>
    () => {
        if (isOpen === shouldOpen && !force) return;
        setIsOpenStateUpdaters.forEach(set => set(shouldOpen));

        if (!shouldOpen) afterClose?.();
    };

type UseAutocompleteApiProps = {
    searchPath: string;
    productsIndex: string;
    contentPagesIndex: string;
    querySuggestionsIndex: string;
    autoCompleteProps?: AutocompleteOptionsWithMetadata<BaseItem>;
    autocompleteRef: MutableRefObject<AutocompleteRef>;
    initialProducts?: Array<ProductAlgoliaModel>;
};

const redirectPlugin = createRedirectUrlPlugin();

const useAutocompleteApi = ({
    searchPath,
    productsIndex,
    contentPagesIndex,
    querySuggestionsIndex,
    autoCompleteProps,
    autocompleteRef,
    initialProducts,
}: UseAutocompleteApiProps) => {
    const { t } = useGranite();
    const isSearchPageTemplate = isPageTemplate(PageTemplates.SEARCH);

    const searchClient = useSearchClient();

    const autocompleteStateRef = useRef<
        Partial<AutocompleteState<BaseItem>> | undefined
    >({
        query: isSearchPageTemplate ? parseUrl().q : "",
    });

    const [openState, setOpenState] = useState(false);
    const [autocompleteState, setAutocompleteState] = useState<{
        items: Record<string, BaseItem[]>;
        autocompleteState: AutocompleteState<BaseItem>;
        autocompleteActions: AutocompleteActions;
    }>();

    const getRedirectUrl = useCallback(() => {
        if (!isDefined(autocompleteStateRef.current)) return;

        return getRedirectUrlFromState(autocompleteStateRef.current);
    }, [autocompleteStateRef]);

    const { submit } = useSubmitAutocompleteSearchBox(
        searchPath,
        getRedirectUrl,
    );

    const autocomplete = useMemo(
        () =>
            createAutocomplete<
                BaseItem,
                BaseSyntheticEvent,
                MouseEvent,
                KeyboardEvent
            >({
                ...autoCompleteProps,
                placeholder: t("searchBox.input.placeholder"),
                defaultActiveItemId: null,
                plugins: [redirectPlugin],
                openOnFocus: true,
                initialState: {
                    ...autocompleteStateRef.current,
                    isOpen: openState,
                    query:
                        autocompleteRef.current.query ??
                        autocompleteStateRef.current?.query,
                },
                onStateChange({
                    state,
                    refresh,
                    setActiveItemId,
                    setQuery,
                    setIsOpen,
                }) {
                    autocompleteStateRef.current = state;

                    const items = state.collections.reduce<
                        Record<string, BaseItem[]>
                    >(
                        (itemsAcc, collection) => ({
                            ...itemsAcc,
                            [collection.source.sourceId]: collection.items,
                        }),
                        {},
                    );

                    setAutocompleteState({
                        items: items,
                        autocompleteState: state,
                        autocompleteActions: {
                            refresh: refresh,
                            setActiveItemId: setActiveItemId,
                            setQuery: (query: string) => {
                                if (autocompleteStateRef.current) {
                                    autocompleteStateRef.current.query = query;
                                }

                                autocompleteRef.current.query = query;
                                setQuery(query);
                            },
                            submit: (query: string) => {
                                autocompleteRef.current.query = query;
                                submit(query);
                                createSetAutocompleteOpen(
                                    [setIsOpen, setOpenState],
                                    openState,
                                )(false, true)();
                            },
                        },
                    });
                },
                shouldPanelOpen(): boolean {
                    return true;
                },
                onSubmit({ state, setIsOpen }) {
                    submit(state.query);
                    createSetAutocompleteOpen(
                        [setIsOpen, setOpenState],
                        openState,
                    )(false, true)();
                },
                getSources({ query, state }) {
                    const showRecentSearches = !query && hasRecentSearches();

                    const sources: Array<
                        AutocompleteSource<any> | boolean | undefined
                    > = [];
                    if (showRecentSearches) {
                        sources.push(recentSearchesSource);
                    }
                    if (querySuggestionsIndex) {
                        sources.push(
                            querySuggestionsSource(
                                querySuggestionsIndex,
                                searchClient,
                            ),
                        );
                    }
                    if (contentPagesIndex && !!query?.trim()) {
                        sources.push(
                            contentPagesSource(contentPagesIndex, searchClient),
                        );
                    }
                    if (productsIndex) {
                        sources.push(
                            productsSource(
                                productsIndex,
                                searchClient,
                                initialProducts,
                            ),
                        );
                    }

                    return !query || !state.isOpen
                        ? sources
                        : debouncedPromise(sources);
                },
            }),
        [
            productsIndex,
            contentPagesIndex,
            querySuggestionsIndex,
            autoCompleteProps,
            t,
            searchClient,
            submit,
            setAutocompleteState,
        ],
    );

    const setAutocompleteOpen = createSetAutocompleteOpen(
        [autocomplete.setIsOpen, setOpenState],
        openState,
        () => autocompleteRef.current.input?.focus(),
    );

    return {
        autocomplete: autocomplete,
        isOpen: openState,
        open: setAutocompleteOpen(true),
        close: setAutocompleteOpen(false),
        ...autocompleteState,
    };
};

export { useAutocompleteApi };
